import { useState } from 'react';
import { LiaCalendar } from 'react-icons/lia';
import { useOutletContext } from 'react-router-dom';

import { EmptyState, EmptyStateBody, EmptyStateHeading } from '@/components-new/empty-state';
import { SectionDescription, SectionHeading } from '@/components-new/section';
import { SegmentedControl } from '@/components-new/segmented-control';
import { MeetingDetailsDrawer, MeetingsList } from '@/features/calendar/components/meetings';
import { Meeting } from '@/features/calendar/types/meeting';
import { useClassificationMeetings } from '@/features/classifications/api';
import { Classification } from '@/features/classifications/types/classification';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useParams } from '@/router';

type OutletContext = { classification: Classification | undefined };

const ClassificationMeetingsPage = () => {
  const { id } = useParams('/classifications/:id');
  const classificationId = parseInt(id, 10);

  const { classification } = useOutletContext<OutletContext>();
  const { user: currentUser } = useCurrentUser();
  const isAiEnabled = currentUser.premiumFeatures.includes('ai_insights');

  const [timeFilter, setTimeFilter] = useState<'upcoming' | 'previous'>('upcoming');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | undefined>();

  const { data: meetings, isLoading } = useClassificationMeetings({
    id: classificationId,
    timeRange: timeFilter,
  });

  const emptyState = (
    <EmptyState>
      <LiaCalendar className="size-12 text-gray-500" />
      <EmptyStateHeading>No {timeFilter} meetings</EmptyStateHeading>
      <EmptyStateBody>
        There are no {timeFilter} meetings for the {classification?.name} classification.
      </EmptyStateBody>
    </EmptyState>
  );

  return (
    <div className="mt-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <SectionHeading level={3}>
            {timeFilter === 'upcoming' ? 'Upcoming' : 'Previous'} Meetings
          </SectionHeading>
          <SectionDescription>
            Board meetings discussing the {classification?.name} classification.
          </SectionDescription>
        </div>
        <SegmentedControl
          items={[
            { label: 'Upcoming', value: 'upcoming' },
            { label: 'Previous', value: 'previous' },
          ]}
          value={timeFilter}
          onChange={(v) => setTimeFilter(v as 'upcoming' | 'previous')}
        />
      </div>

      <div className="relative min-h-24">
        <MeetingsList
          isLoading={isLoading}
          meetings={meetings}
          emptyState={emptyState}
          onShowMeetingDetails={setSelectedMeeting}
          isAiEnabled={isAiEnabled}
        />
      </div>

      <MeetingDetailsDrawer
        meeting={selectedMeeting}
        onClose={() => setSelectedMeeting(undefined)}
      />
    </div>
  );
};

export default ClassificationMeetingsPage;
